import { Typography, Grid } from "@material-ui/core";
import SanitizedGrid from "components/SanitizedGrid";
import {
  SimpleForm,
  ReferenceInput,
  AutocompleteInput,
  required,
  TextInput,
  EditProps,
  useEditController,
  SelectInput,
  FormDataConsumer,
} from "react-admin";
import { GetParam } from "utils/tools";
import CustomDatePickerInput from "../../components/CustomDatePickerInput";
import React, { useEffect, useState } from "react";
import CustomEditToolbar from "../../components/CustomEditToolbar";
import moment from "moment/moment";
import CustomNumberInput from "components/CustomNumberInput";
import SubcontractorAdhocCategoryInput from "./components/SubcontractorAdhocCategoryInput";
import PermissionLoading from "../../components/PermissionLoading";
import EditWrapper from "../../components/EditWrapper";
import Alert from "@material-ui/lab/Alert";
import CustomMonthPickerInput from "../../components/CustomMonthPickerInput";
import { fetchAllData } from "../../dataProvider";

const SubcontractorAdhocFeeEdit = (props: EditProps) => {
  const { id } = props;
  const param = GetParam();
  const { record, loading } = useEditController(props);

  const [excludedDates, setExcludedDates] = useState<Date[]>([]);

  // Fetch all the non pending subcontractor purchase invoice summaries
  // than populate the excludedDates based on non pending subcontractor purchase invoice summaries
  useEffect(() => {
    fetchAllData("subcontractorPurchaseInvoiceSummaries", {
      StatusIn: [
        "GeneratingSubcontractorPurchaseInvoice",
        "ReadyToApprove",
        "Approved",
      ],
    })
      .then((res) => {
        const dates = res.map(
          (summary: any) => new Date(summary.year, summary.month - 1, 1)
        );
        setExcludedDates(dates);
      });
  }, []);

  const breadcrumb = [
    {
      title: "Subcontractors",
      link: `/subcontractors/${param.get("subcontractorId")}/show`,
    },
    {
      title: "Financial",
      link: `/subcontractors/${param.get("subcontractorId")}/show/2`,
    },
    {
      title: id,
      link: "",
    },
  ];

  const transform = async (data: any) => {
    if (data.price) {
      data.amount = Number(Math.round(Number(data.price) * 100.0));
    }

    if (data.typePeriod !== "date") {
      data.date = null;
    }

    delete data.planningIssuedAdhocFee;

    return data;
  };

  const formatRecord = (res: any) => {
    if (res) {
      if (res.amount) {
        res.price = res.amount.toFixed(2) / 100;
      }

      if (res.date) {
        res.typePeriod = "date";
      } else {
        res.typePeriod = "custom";
      }

      return res;
    }
  };

  if (loading) {
    return <PermissionLoading />;
  }

  return (
    <>
      <EditWrapper
        breadcrumb={breadcrumb}
        isDefaultTitle
        title="Create Subcontractor Adhoc Fee"
        actions={null}
        transform={transform}
        {...props}
      >
        <SimpleForm
          record={formatRecord(record)}
          toolbar={
            <CustomEditToolbar
              redirectLink={`/subcontractors/${param.get(
                "subcontractorId"
              )}/show/2`}
              isShowDelete={false}
            />
          }
        >
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">General Info</Typography>
            </Grid>

            <Grid item xs={4}>
              <ReferenceInput
                label="Subcontractor"
                source="subcontractorId"
                reference="subcontractors"
                validate={required()}
                disabled
              >
                <AutocompleteInput optionText="name" />
              </ReferenceInput>
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <ReferenceInput
                label="School"
                source="schoolId"
                reference="schools"
                validate={required()}
                filterToQuery={(searchText: any) => {
                  if (searchText !== "") return { search: searchText };
                }}
                disabled
              >
                <AutocompleteInput optionText="name" />
              </ReferenceInput>
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Adhoc Fee Detail</Typography>
            </Grid>

            <Grid item xs={4}>
              <SelectInput
                source="typePeriod"
                choices={[
                  { id: "date", name: "By Date" },
                  { id: "custom", name: "Custom Period" },
                ]}
                validate={required()}
              />
            </Grid>
            <Grid item xs={8}></Grid>

            <FormDataConsumer>
              {({ formData }) => (
                <>
                  {formData.typePeriod === "date" && (
                    <>
                      <Grid item xs={4}>
                        <CustomDatePickerInput
                          name="date"
                          label="Date"
                          max={moment().format("Y-MM-DD")}
                        />
                      </Grid>
                      <Grid item xs={8}></Grid>
                    </>
                  )}

                  {formData.typePeriod === "custom" && (
                    <>
                      <Grid item xs={4}>
                        <TextInput
                          source="period"
                          label="Custom Period (Free text)"
                          validate={required()}
                        />
                      </Grid>
                      <Grid item xs={8}></Grid>
                    </>
                  )}

                  <Grid item xs={4}>
                    <CustomMonthPickerInput
                      name="planningIssuedAdhocFee"
                      label="Planning Issued Adhoc Fee"
                      month={formData.planningIssuedMonth}
                      year={formData.planningIssuedYear}
                      monthFieldName="planningIssuedMonth"
                      yearFieldName="planningIssuedYear"
                      excludeDates={excludedDates}
                      isRequired
                    />
                  </Grid>
                  <Grid item xs={8}></Grid>
                </>
              )}
            </FormDataConsumer>

            <Grid item xs={4}>
              <TextInput
                source="routeName"
                label="Route Name (Free text)"
                validate={required()}
              />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <SubcontractorAdhocCategoryInput />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <CustomNumberInput label="Amount ($)" source="price" isRequired />
              <Alert severity="info" style={{ marginTop: "-20px", marginBottom: "25px" }}>
                By default, the amount entered is considered a positive value.
                <br/>
                If you want to create a deduction (negative value), please prefix
                the amount with a minus sign (e.g., <strong>-100</strong>).
              </Alert>
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <TextInput
                multiline={true}
                label="Remarks"
                source="remarks"
                inputProps={{ maxLength: 90 }}
                validate={required()}
              />
            </Grid>
            <Grid item xs={8}></Grid>
          </SanitizedGrid>
        </SimpleForm>
      </EditWrapper>
    </>
  );
};

export default SubcontractorAdhocFeeEdit;
